import { usePage } from "@inertiajs/react";
import { IconCheckmark1Small } from "central-icons/IconCheckmark1Small";
import { useEffect, useState } from "react";
import CountryFlag from "@/components/localization/flag";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { Input } from "@/components/ui/input";
import { useTranslations } from "@/hooks/use-translations";
import { VALUE_ALL } from "@/lib/constants";
import { cn } from "@/lib/utils";
import type { Language, SharedData } from "@/types";

type SelectLanguageMenuProps = {
  initialValue: string;
  onSelect: (language: string) => void;
  variant?: "default" | "gray";
  showAllOption?: boolean;
};

export type LanguageComboBoxOptionType = ComboboxOption & {
  flag?: string;
};

const LanguageOptionFlag = ({
  option,
}: {
  option: LanguageComboBoxOptionType;
}) => {
  if (option.flag) {
    return option.flag;
  }

  return <CountryFlag languageOption={option} />;
};

const LanguageComboBoxOption = ({
  option,
  selectedLanguage,
}: {
  option: LanguageComboBoxOptionType;
  selectedLanguage: string;
}) => (
  <>
    <IconCheckmark1Small
      className={cn(
        "mr-2 size-4",
        selectedLanguage === option.value ? "opacity-100" : "opacity-0"
      )}
    />
    <span className="mr-2">
      <LanguageOptionFlag option={option} />
    </span>
    <span>{option.label}</span>
  </>
);

const LanguageComboBoxTrigger = ({
  option,
}: {
  option?: LanguageComboBoxOptionType;
}) => {
  if (!option) {
    return null;
  }

  return (
    <span className="flex items-center gap-2">
      <LanguageOptionFlag option={option} />
      <span>{option.label}</span>
    </span>
  );
};

export default function SelectLanguageMenu({
  initialValue,
  onSelect,
  variant = "default",
  showAllOption = false,
}: SelectLanguageMenuProps) {
  const { config } = usePage<SharedData>().props;
  const { __ } = useTranslations();

  const [selectedLanguage, setSelectedLanguage] =
    useState<string>(initialValue);
  const [languageOptions, setLanguageOptions] = useState<Language[]>([]);

  const setLanguage = (language: string) => {
    setSelectedLanguage(language);

    onSelect(language);
  };

  useEffect(() => {
    setLanguageOptions(config.localization.languageOptions);
  }, [config.localization.languageOptions]);

  if (!languageOptions.length) {
    return null;
  }

  const comboboxOptions: LanguageComboBoxOptionType[] = [
    ...(showAllOption
      ? [
          {
            value: VALUE_ALL,
            label: __("localization.form_elements.language.all_languages"),
            flag: "🌐",
            searchLabel: __(
              "localization.form_elements.language.all_languages"
            ),
          },
        ]
      : []),
    ...languageOptions.map((lang) => ({
      value: lang.value,
      label: lang.label,
      searchLabel: lang.label,
    })),
  ];

  return (
    <>
      <Input name="language" type="hidden" value={selectedLanguage} />
      <Combobox
        className={variant === "gray" ? "bg-input" : "bg-white"}
        emptyText={__("general.no_results_found")}
        onValueChange={setLanguage}
        options={comboboxOptions}
        placeholder={__("localization.form_elements.language.placeholder")}
        renderOption={(option) => (
          <LanguageComboBoxOption
            option={option as LanguageComboBoxOptionType}
            selectedLanguage={selectedLanguage}
          />
        )}
        renderTrigger={(option) => (
          <LanguageComboBoxTrigger
            option={option as LanguageComboBoxOptionType}
          />
        )}
        searchPlaceholder={__("general.search")}
        value={selectedLanguage}
      />
    </>
  );
}
